/**
 * The project workspace's tab row on desktop (design "Flow Project Redesign", Tabs board; utils/projectTabsModel.js):
 * Home · Apps · Files · Preview (with the avatars of whoever is looking at a preview), the advanced tabs on the row
 * (pinned, or shown by themselves: a dot and a one-time note when one arrives; Quality with its red count of the
 * release-gate rules that fail), the temporary one (opened for now: Pin and Close), a divider, then one More ▾ button
 * (2026-10-30: it was one menu per step, Build ▾ · Verify ▾ · Ship ▾ · Operate ▾). More opens one panel with the steps
 * of the work as headings, Build · Verify · Ship · Operate: each lists its advanced tabs with a line and a pin (Logs,
 * Jobs and FinOps among them, in Operate), then the header's actions for that step (`groupActions`: Publish and deploys
 * in Ship, which shows only with it; Backups, Usage in Operate); Build ends with History (Changes, Activity and Sessions
 * in one tab, always). The counts of every step add up on More (History's new changes, the blocking rules, what waits).
 * Where the row has no room, the tabs past the edge fold into More ("+N", listed on top of its panel); the one on never
 * does. Team is not here: it is a menu of the project row, next to Publish (`TeamMenu`, below). On the right only the
 * current view's tools (`tools`, or what the Home portals into `toolsSlotRef`). The "In this Project" toggle and full
 * screen are in the project row above.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Switch } from 'antd';
import { CaretDown, EyeSlash, PushPinSimple, SidebarSimple, X } from '@phosphor-icons/react';
import { DEFAULT_TAB_PREFS, HISTORY_IDS, TAB_GROUPS, TEAM_GROUP, buildTabRow, foldTabs, visibleAdvanced } from '../../utils/projectTabsModel';
import { firstName } from '../../utils/presenceModel';
import { ParticipantMark } from '../presence/Presence';
import { SearchHint } from '../search/SearchContext';
import './Workspace.css';

const noop = () => {};
// The row with nothing known about the project and nothing chosen: the default tabs, More with its pins inert (no
// Terminal nor Logs: they are offered only once the project says the person may open them).
const STATIC_TABS = {
  row: buildTabRow(),
  more: visibleAdvanced(['terminal', 'logs']).map((tab) => ({ ...tab, mode: DEFAULT_TAB_PREFS.modes[tab.id], shown: null, reason: null, pinned: false, count: 0 })),
  temp: null,
  note: null,
  allPinned: false,
  acknowledge: noop,
  hide: noop,
  togglePin: noop,
  pinTemp: noop,
  closeTemp: noop,
  setAllPinned: noop,
};
const DIVIDER = 17;
const SLACK = 8;

const countText = (count) => (count > 99 ? '99+' : count);
// The panel's widest (Workspace.css .workspace-more.is-all: up to this, never more than the row less 24px), to keep it
// inside the row when the button is far right.
const PANEL_WIDTH = 900;
// Where the panel starts: under the button, moved left as much as its width needs.
const panelLeft = (anchor, rowWidth) => Math.max(0, Math.min(anchor, rowWidth - Math.min(PANEL_WIDTH, rowWidth - 24) - 12));

/**
 * A menu panel's keyboard and pointer behaviour: Escape and a click outside close it, it opens with the focus on its
 * first control, and the focus comes back to its button (`buttonSelector`) after Escape.
 */
function useMenuPanel(open, close, panelRef, buttonSelector) {
  useEffect(() => {
    if (!open) { return undefined; }
    const onKey = (event) => {
      if (event.key === 'Escape') { close(); document.querySelector(buttonSelector)?.focus(); }
    };
    const onDown = (event) => {
      if (!panelRef.current?.contains(event.target) && !event.target.closest?.(buttonSelector)) { close(); }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    panelRef.current?.querySelector('button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close, panelRef, buttonSelector]);
}

/** A header action as a row of a menu panel (Publish and deploys, Backups, Share…): like a tab's, without a pin. */
function ActionRow({ action, onDone }) {
  return (
    <div className="workspace-more-item">
      <button className="workspace-more-row" data-testid={`more-action-${action.id}`} onClick={() => { onDone(); action.onSelect(); }} type="button">
        <span className="workspace-more-name">
          {action.label}
          {action.count > 0 && <span aria-label={`${action.count} waiting`} className="workspace-tab-count">{countText(action.count)}</span>}
        </span>
        {action.desc && <span className="workspace-more-desc">{action.desc}</span>}
      </button>
      {/* No pin, but the same slot: every row is as wide as the next. */}
      <span aria-hidden="true" className="workspace-more-tools" />
    </div>
  );
}

ActionRow.propTypes = {
  action: PropTypes.shape({ count: PropTypes.number, desc: PropTypes.string, id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, onSelect: PropTypes.func.isRequired }).isRequired,
  onDone: PropTypes.func.isRequired,
};

export default function WorkspaceTabs({
  current,
  onSelect,
  changesCount = 0,
  previewWatchers = [],
  previewDisabled = false,
  tools = null,
  toolsSlotRef = null,
  tabs = null,
  onOpenSettings = null,
  groupActions = {},
}) {
  const layout = tabs || STATIC_TABS;
  const { row, more } = layout;
  const rowRef = useRef(null);
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const widths = useRef({});
  const [room, setRoom] = useState(0);
  const [measured, setMeasured] = useState(0);
  // Whether More's panel is open.
  const [open, setOpen] = useState(false);
  const [menuLeft, setMenuLeft] = useState(0);
  const [noteLeft, setNoteLeft] = useState(0);

  // The row's width, as it changes.
  useEffect(() => {
    const node = rowRef.current;
    if (!node || typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(() => setRoom(Math.max(0, node.clientWidth - 24)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // Each tab's width while it is on the row (a folded one keeps the last it had); after every render, a new state only
  // when a width changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) { return; }
    let changed = false;
    nav.querySelectorAll('[data-tab-id]').forEach((node) => {
      const width = node.offsetWidth;
      if (width && widths.current[node.dataset.tabId] !== width) { widths.current[node.dataset.tabId] = width; changed = true; }
    });
    const moreWidth = [...nav.querySelectorAll('[data-group-button]')].reduce((sum, node) => sum + node.offsetWidth, 0);
    if (moreWidth && widths.current.__more !== moreWidth) { widths.current.__more = moreWidth; changed = true; }
    if (changed) { setMeasured((n) => n + 1); }
  });
  // The one-time note sits under the tab that arrived.
  const noteId = layout.note?.id || null;
  useLayoutEffect(() => {
    const node = noteId ? navRef.current?.querySelector(`[data-tab-id="${noteId}"]`) : null;
    setNoteLeft(node ? Math.max(0, node.offsetLeft - (navRef.current?.scrollLeft || 0)) : 0);
  }, [noteId, measured]);

  const items = [
    ...row.build.map((tab) => ({ id: tab.id, foldable: tab.kind !== 'build' })),
  ];
  const folded = measured >= 0 ? foldTabs(items, {
    widths: widths.current, available: room, reserve: DIVIDER + SLACK + (widths.current.__more || 90), active: current,
  }) : [];
  const isFolded = (id) => folded.includes(id);
  const allTabs = [...row.build];

  // More: Escape and a click outside close it; it opens with the focus on its first control, which comes back after.
  const closeMore = React.useCallback(() => setOpen(false), []);
  useMenuPanel(open, closeMore, menuRef, '[data-group-button="more"]');

  const toggleMore = (event) => {
    if (!open) {
      const left = (event.currentTarget.offsetLeft || 0) - (navRef.current?.scrollLeft || 0);
      setMenuLeft(panelLeft(left, rowRef.current?.clientWidth || 0));
    }
    setOpen(!open);
  };
  const choose = (id) => {
    setOpen(false);
    onSelect(id);
  };
  // A tab page's "<Step>" crumb (WorkspaceTitle, `flow:open-tab-group`): More opens with the focus on that step's group.
  const [focusGroup, setFocusGroup] = useState(null);
  useEffect(() => {
    const openGroup = (event) => {
      const button = navRef.current?.querySelector('[data-group-button="more"]');
      const left = (button?.offsetLeft || 0) - (navRef.current?.scrollLeft || 0);
      setMenuLeft(panelLeft(left, rowRef.current?.clientWidth || 0));
      setFocusGroup(event?.detail?.group || null);
      setOpen(true);
    };
    window.addEventListener('flow:open-tab-group', openGroup);
    return () => window.removeEventListener('flow:open-tab-group', openGroup);
  }, []);
  useEffect(() => {
    if (!open || !focusGroup) { return; }
    const group = menuRef.current?.querySelector(`[data-testid="more-group-${focusGroup}"]`);
    group?.scrollIntoView?.({ block: 'nearest' });
    group?.querySelector('button')?.focus();
    setFocusGroup(null);
  }, [open, focusGroup]);
  const closeTemp = (id) => {
    layout.closeTemp();
    if (current === id) { onSelect('dashboard'); }
  };

  const tabButton = (tab, extra = {}) => {
    const on = current === tab.id;
    const disabled = tab.id === 'preview' && previewDisabled;
    const watchers = tab.id === 'preview' ? previewWatchers : [];
    const isHistory = tab.id === 'history' || tab.id === 'changes';
    const count = isHistory ? changesCount : tab.count || 0;
    return (
      <button
        aria-current={on ? 'page' : undefined}
        className={`workspace-tab${on ? ' is-on' : ''}${tab.kind === 'temp' ? ' is-temp' : ''}`}
        data-testid={`workspace-tab-${tab.id}`}
        disabled={disabled}
        onClick={() => onSelect(tab.id)}
        title={disabled ? 'This project has no service to preview yet' : tab.kind === 'temp' ? 'Opened for now: close it or pin it to keep it' : undefined}
        type="button"
        {...extra}
      >
        {/* As wide as its bold self, so the tab on does not push the others. */}
        <span className="workspace-tab-label" data-label={tab.label}>{tab.label}</span>
        {count > 0 && isHistory && <span aria-label={`${count} new`} className="workspace-tab-count">{countText(count)}</span>}
        {count > 0 && !isHistory && <span aria-label={`${count} blocking`} className="workspace-tab-count is-bad">{countText(count)}</span>}
        {tab.dot && <span aria-label="new" className="workspace-tab-dot" role="img" />}
        {watchers.length > 0 && (
          <span className="workspace-tab-marks" title={`${watchers.map((p) => firstName(p.name)).join(', ')} on the preview`}>
            {watchers.slice(0, 2).map((person) => (
              <ParticipantMark initials={person.initials} key={person.id} label={`${person.name} is on the preview`} size={18} slot={person.slot} />
            ))}
          </span>
        )}
      </button>
    );
  };

  const renderTab = (tab) => {
    if (isFolded(tab.id)) { return null; }
    if (tab.kind === 'temp') {
      return (
        <span className="workspace-tab-temp" data-tab-id={tab.id} data-testid="workspace-temp-tab" key={tab.id}>
          {tabButton(tab)}
          <button
            aria-label={`Pin ${tab.label}`}
            aria-pressed="false"
            className="workspace-tab-icon"
            data-testid={`workspace-tab-pin-${tab.id}`}
            onClick={layout.pinTemp}
            title="Pin to keep it"
            type="button"
          >
            <PushPinSimple aria-hidden size={14} />
          </button>
          <button
            aria-label={`Close ${tab.label}`}
            className="workspace-tab-icon"
            data-testid={`workspace-tab-close-${tab.id}`}
            onClick={() => closeTemp(tab.id)}
            title="Close"
            type="button"
          >
            <X aria-hidden size={12} />
          </button>
        </span>
      );
    }
    return <React.Fragment key={tab.id}>{tabButton(tab, { 'data-tab-id': tab.id })}</React.Fragment>;
  };

  const note = layout.note;
  const foldedTabs = allTabs.filter((tab) => isFolded(tab.id));
  const menuId = 'workspace-more-menu';
  // Ship has no tab since Configuration moved to Build: it shows with Publish and deploys (not for a viewer, who would
  // read an empty heading). Team is the project row's menu (TeamMenu), not a step here.
  const hasSomething = (group) => more.some((tab) => tab.group === group.id) || groupActions[group.id]?.length > 0;
  const groups = TAB_GROUPS.filter((group) => group.id !== 'ship' || hasSomething(group))
    .map((group) => ({ ...group, items: more.filter((tab) => tab.group === group.id), actions: groupActions[group.id] || [] }));
  const hasHistory = row.history.length > 0;
  // More is "here" for a tab opened from its panel, not for one that has its own place on the row.
  const onRow = row.build.some((tab) => tab.id === current);
  const here = !onRow && (groups.some((group) => group.items.some((tab) => tab.id === current))
    || (hasHistory && (current === 'history' || HISTORY_IDS.includes(current))));
  // Every step's counts on the one button: History's new changes, the blocking rules, what waits in the actions.
  const blocking = groups.reduce((sum, group) => sum + group.items.reduce((n, tab) => n + (tab.count || 0), 0), 0);
  const waiting = groups.reduce((sum, group) => sum + group.actions.reduce((n, action) => n + (action.pending || 0), 0), 0);
  const moreLabel = [
    'More tabs and actions',
    folded.length > 0 ? `${folded.length} with no room on the row` : null,
    hasHistory && changesCount > 0 ? `${changesCount} new changes` : null,
    blocking > 0 ? `${blocking} blocking` : null,
    waiting > 0 ? `${waiting} waiting` : null,
  ].filter(Boolean).join(', ');

  return (
    <div className="workspace-tabs" data-testid="workspace-tabs" ref={rowRef}>
      <nav aria-label="Project views" className="workspace-tabs-nav" ref={navRef}>
        {row.build.map(renderTab)}
        {/* The rest of the tabs by step of the work, after a divider: each group opens its menu. */}
        <span aria-hidden="true" className="workspace-tabs-divider" />
        <button
          aria-controls={open ? menuId : undefined}
          aria-current={here ? 'page' : undefined}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={moreLabel}
          className={`workspace-tab workspace-tab-more is-group-more${open ? ' is-open' : ''}${here ? ' is-on' : ''}`}
          data-group-button="more"
          data-testid="workspace-tab-more"
          onClick={toggleMore}
          title="Build, Verify, Ship and Operate: every other tab and action of the project"
          type="button"
        >
          <span>More</span>
          {folded.length > 0 && <span aria-label={`${folded.length} more tabs`} className="workspace-tab-count is-neutral" title="Tabs with no room on the row are listed here">+{folded.length}</span>}
          {hasHistory && changesCount > 0 && <span aria-label={`${changesCount} new`} className="workspace-tab-count">{countText(changesCount)}</span>}
          {blocking > 0 && <span aria-label={`${blocking} blocking`} className="workspace-tab-count is-bad">{countText(blocking)}</span>}
          {waiting > 0 && <span aria-label={`${waiting} waiting`} className="workspace-tab-count">{countText(waiting)}</span>}
          <CaretDown aria-hidden size={13} />
        </button>
      </nav>
      {open && (
        <div aria-label="More tabs" className="workspace-more is-all" data-testid="workspace-more-menu" id={menuId} ref={menuRef} role="dialog" style={{ left: menuLeft }}>
          {/* The tabs with no room on the row, on top. */}
          {foldedTabs.length > 0 && (
            <div className="workspace-more-group">
              <div className="workspace-more-head"><span className="workspace-more-cap">No room on the row</span></div>
              {foldedTabs.map((tab) => (
                <div className="workspace-more-item" key={tab.id}>
                  <button className="workspace-more-row is-plain" data-testid={`more-folded-${tab.id}`} onClick={() => choose(tab.id)} type="button">
                    <span className="workspace-more-name">{tab.label}</span>
                    {(tab.id === 'history' || tab.id === 'changes') && changesCount > 0 && <span className="workspace-tab-count">{countText(changesCount)}</span>}
                    {tab.count > 0 && <span className="workspace-tab-count is-bad">{countText(tab.count)}</span>}
                  </button>
                  <span aria-hidden="true" className="workspace-more-tools" />
                </div>
              ))}
            </div>
          )}
          <div className="workspace-more-groups">
          {groups.map((group) => (
            <div className="workspace-more-group" data-testid={`more-group-${group.id}`} key={group.id} role="group" aria-label={group.label}>
              <div className="workspace-more-head is-group"><span className="workspace-more-cap">{group.label}</span><span className="workspace-more-hint">{group.hint}</span></div>
              {group.items.map((tab) => (
            <div className="workspace-more-item" key={tab.id}>
              <button className="workspace-more-row" data-testid={`more-open-${tab.id}`} onClick={() => choose(tab.id)} type="button">
                <span className="workspace-more-name">
                  {tab.label}
                  {tab.count > 0 && <span aria-label={`${tab.count} blocking`} className="workspace-tab-count is-bad">{countText(tab.count)}</span>}
                  {tab.reason && <span className="workspace-more-tag">On the row: {tab.reason}</span>}
                </span>
                <span className="workspace-more-desc">{tab.desc}</span>
              </button>
              {/* A fixed slot on the right (Hide, Pin): shown on hover, focus or when on, its space kept otherwise. */}
              <span className={`workspace-more-tools${tab.pinned || tab.shown ? ' is-on' : ''}`}>
                {/* Shown on the row (pinned, or Auto because the project needs it): Hide puts it back in More only, so
                    it stays out of the row even when the project would bring it there. */}
                {(tab.pinned || tab.shown) && (
                  <button
                    aria-label={`Hide ${tab.label}`}
                    className="workspace-more-pin workspace-more-hide"
                    data-testid={`more-hide-${tab.id}`}
                    onClick={() => layout.hide(tab.id)}
                    title="Hide: only in More, never on the row by itself"
                    type="button"
                  >
                    <EyeSlash aria-hidden size={15} />
                  </button>
                )}
                <button
                  aria-label={`${tab.pinned ? 'Unpin' : 'Pin'} ${tab.label}`}
                  aria-pressed={tab.pinned}
                  className={`workspace-more-pin${tab.pinned ? ' is-on' : ''}${!tab.pinned && tab.shown === 'auto' ? ' is-auto' : ''}`}
                  data-testid={`more-pin-${tab.id}`}
                  onClick={() => layout.togglePin(tab.id)}
                  title={tab.pinned ? 'Pinned: unpin to go back to Auto' : tab.shown === 'auto' ? 'Shown automatically; pin it to keep it whatever happens' : 'Pin to the tab row, in every project'}
                  type="button"
                >
                  <PushPinSimple aria-hidden size={15} weight={tab.pinned ? 'fill' : 'regular'} />
                </button>
              </span>
            </div>
          ))}
              {group.actions.map((action) => <ActionRow action={action} key={action.id} onDone={closeMore} />)}
              {group.id === 'build' && row.history.length > 0 && (
                <div className="workspace-more-item" key="history">
                  <button className="workspace-more-row" data-testid="more-open-history" onClick={() => choose('history')} type="button">
                    <span className="workspace-more-name">History{changesCount > 0 && <span aria-label={`${changesCount} new`} className="workspace-tab-count">{countText(changesCount)}</span>}</span>
                    <span className="workspace-more-desc">Changes, Activity and Sessions of the project.</span>
                  </button>
                  <span aria-hidden="true" className="workspace-more-tools" />
                </div>
              )}
            </div>
          ))}
          </div>
          <div className="workspace-more-foot">
            <span className="workspace-more-all">
              <Switch checked={layout.allPinned} data-testid="more-all" id="workspace-more-all" onChange={(on) => layout.setAllPinned(on)} size="small" />
              <label htmlFor="workspace-more-all">Show advanced tabs in every project</label>
            </span>
            <SearchHint onBeforeOpen={() => setOpen(false)} />
            {onOpenSettings && (
              <button className="workspace-more-link" onClick={() => { setOpen(false); onOpenSettings(); }} type="button">Tab settings</button>
            )}
          </div>
        </div>
      )}
      {note && !open && (
        <div aria-label={note.title} className="workspace-tab-note" data-testid="workspace-tab-note" role="dialog" style={{ left: noteLeft }}>
          <strong>{note.title}</strong>
          <p>{note.text}</p>
          <div className="workspace-tab-note-actions">
            <button className="workspace-note-btn is-primary" onClick={() => { layout.acknowledge(note.id); onSelect(note.id); }} type="button">Open {note.label}</button>
            <button className="workspace-note-btn" onClick={() => layout.acknowledge(note.id)} type="button">Got it</button>
            <button className="workspace-note-btn is-quiet" onClick={() => layout.hide(note.id)} type="button">Hide this tab</button>
          </div>
        </div>
      )}
      <div className="workspace-tabs-end" data-testid="workspace-view-tools">
        {/* The Home's own tools (Start all, Open app) come from the page itself (DashboardView), through a portal. */}
        {toolsSlotRef && <div className="workspace-tabs-slot" data-testid="workspace-tools-slot" ref={toolsSlotRef} />}
        {tools}
      </div>
    </div>
  );
}

WorkspaceTabs.propTypes = {
  changesCount: PropTypes.number,
  current: PropTypes.string,
  // The header's actions under a step's heading in More, after its tabs: `{ ship: [{ id, label, desc, onSelect, count,
  // pending }], operate }` (`team` is not read here: TeamMenu). `count`: a badge on the row; `pending`: one on More only
  // (the row's label already says it).
  groupActions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    count: PropTypes.number, pending: PropTypes.number, desc: PropTypes.string, id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, onSelect: PropTypes.func.isRequired,
  }))),
  onOpenSettings: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  previewDisabled: PropTypes.bool,
  previewWatchers: PropTypes.array,
  // What useProjectTabs answers (hooks/useProjectTabs.js); none: the default row.
  tabs: PropTypes.object,
  tools: PropTypes.node,
  toolsSlotRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

/** The "In this Project" panel's toggle with how many are here: the right end of the project row. */
export function WorkspacePanelToggle({ panelOpen = false, hereCount = 0, onToggle }) {
  return (
    <button
      aria-expanded={Boolean(panelOpen)}
      aria-label={`${panelOpen ? 'Hide' : 'Show'} who is in this project${hereCount ? ` (${hereCount} here)` : ''}`}
      className={`workspace-tab is-small workspace-panel-toggle${panelOpen ? ' is-on' : ''}`}
      data-testid="workspace-panel-toggle"
      onClick={onToggle}
      title={panelOpen ? 'Hide the In this Project panel' : 'Show who is in this project'}
      type="button"
    >
      <SidebarSimple aria-hidden mirrored size={16} />
      <span>In this Project</span>
      {hereCount > 0 && <span className="workspace-tab-count is-neutral">{hereCount}</span>}
    </button>
  );
}

WorkspacePanelToggle.propTypes = {
  hereCount: PropTypes.number,
  onToggle: PropTypes.func.isRequired,
  panelOpen: PropTypes.bool,
};

/**
 * Team ▾ in the project row, before Publish (2026-10-30: it was the last menu of the tab row, and Share was a button of
 * its own; Share is now Team's first row). `actions`: the header's Team actions (UnifiedTopBar `groupActions.team`:
 * Share, Messages, Notes); nothing when there is none. What waits (`pending`, the suggestions waiting in Notes) is the
 * button's count.
 */
export function TeamMenu({ actions = [] }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const close = React.useCallback(() => setOpen(false), []);
  useMenuPanel(open, close, panelRef, '[data-group-button="team"]');
  if (actions.length === 0) { return null; }
  const waiting = actions.reduce((sum, action) => sum + (action.pending || 0), 0);
  const panelId = 'workspace-team-menu';
  return (
    <span className="workspace-team-menu">
      <button
        aria-controls={open ? panelId : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Team: share and work together${waiting > 0 ? ` (${waiting} waiting)` : ''}`}
        className={`share-top-btn workspace-team-btn${open ? ' is-open' : ''}`}
        data-group-button="team"
        data-testid="workspace-tab-team"
        onClick={() => setOpen(!open)}
        title="Share, messages, comments, suggestions and notes"
        type="button"
      >
        <span>Team</span>
        {waiting > 0 && <span aria-label={`${waiting} waiting`} className="workspace-tab-count">{countText(waiting)}</span>}
        <CaretDown aria-hidden size={12} />
      </button>
      {open && (
        <div aria-label="Team" className="workspace-more is-team" data-testid="workspace-team-panel" id={panelId} ref={panelRef} role="dialog">
          <div aria-label={TEAM_GROUP.label} className="workspace-more-group" data-testid="more-group-team" role="group">
            <div className="workspace-more-head is-group"><span className="workspace-more-cap">{TEAM_GROUP.label}</span><span className="workspace-more-hint">{TEAM_GROUP.hint}</span></div>
            {actions.map((action) => <ActionRow action={action} key={action.id} onDone={close} />)}
          </div>
          <div className="workspace-more-foot"><SearchHint full onBeforeOpen={close} /></div>
        </div>
      )}
    </span>
  );
}

TeamMenu.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    count: PropTypes.number, pending: PropTypes.number, desc: PropTypes.string, id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, onSelect: PropTypes.func.isRequired,
  })),
};
